import './styles.scss';

import { useState, useRef } from 'react';
import { Text, Table, Input, Button, StatusChip, MetricCard, Popper } from '@fragments';
import {
    IconSearch,
    IconClipboardCheck,
    IconUsers,
    IconArrowRight,
    IconCalendar,
    IconX,
    IconChevronDown,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';

dayjs.locale('pt-br');

export default function SubmissionsView({
    submissions,
    insights,
    loading,
    page,
    pages,
    records,
    search,
    formId,
    forms,
    handleSearch,
    handleSelectPage,
    handleViewSubmission,
    handleFormFilter,
}) {
    const [inputValue, setInputValue] = useState(search);
    const formPopperRef = useRef();
    const formTriggerRef = useRef();

    const selectedForm = forms.find(f => f.id === formId) ?? null;

    function commitSearch() {
        if (inputValue !== search) handleSearch(inputValue);
    }

    function handleKeyDown(e) {
        if (e.key === 'Enter') commitSearch();
    }

    function openFormPopper(e) {
        e.stopPropagation();
        const options = forms.map(form => ({
            id: form.id,
            label: form.title,
            onClick: () => handleFormFilter(form.id),
        }));
        formPopperRef.current.open(options, 'bottom-start', formTriggerRef.current);
    }

    function clearFormFilter(e) {
        e.stopPropagation();
        handleFormFilter(null);
    }

    const columns = [
        {
            id: 'form_title',
            title: 'Formulário',
            grow: 1,
            customRender: (item) => (
                <div className="submission-form-cell">
                    <div className="submission-form-icon">
                        <IconClipboardCheck size={14} />
                    </div>
                    <Text.span contrast={8}>{item.form?.title || '—'}</Text.span>
                </div>
            ),
        },
        {
            id: 'username',
            title: 'Usuário',
            minWidth: 180,
            customRender: (item) => (
                <Text.span contrast={7}>{item.user?.username || '—'}</Text.span>
            ),
        },
        {
            id: 'email',
            title: 'Email',
            minWidth: 200,
            customRender: (item) => (
                <Text.span contrast={5}>{item.user?.email || '—'}</Text.span>
            ),
        },
        {
            id: 'completed_at',
            title: 'Respondido em',
            minWidth: 160,
            customRender: (item) => {
                if (item.completed && item.completed_at) {
                    return (
                        <Text.span contrast={5}>
                            {dayjs(item.completed_at).format('DD/MM/YYYY HH:mm')}
                        </Text.span>
                    );
                }
                return <StatusChip.Alert text="Em andamento" />;
            },
        },
    ];

    const formsFilledValue = insights?.forms_filled ?? 0;
    const uniqueUsersValue = insights?.unique_users ?? 0;
    const growthWeek = insights?.growth_last_week ?? 0;
    const growthMonth = insights?.growth_last_month ?? 0;

    return (
        <div className="submissions-view px-4">

            <div className="submissions-insights">
                <div className="insights-cards-row">
                    <MetricCard
                        title="Formulários preenchidos"
                        leftIcon={<IconClipboardCheck size={15} />}
                        value={formsFilledValue}
                        variant="number"
                        growth={growthWeek}
                        growthLabel="na semana"
                    />
                    <MetricCard
                        title="Usuários únicos"
                        leftIcon={<IconUsers size={15} />}
                        value={uniqueUsersValue}
                        variant="number"
                    />
                    <MetricCard
                        title="Crescimento mensal"
                        leftIcon={<IconCalendar size={15} />}
                        value={`${growthMonth}%`}
                        growth={growthMonth}
                    />
                </div>

                <div className="insight-search">
                    <div
                        ref={formTriggerRef}
                        className={`form-filter-trigger${selectedForm ? ' has-value' : ''}`}
                        onClick={openFormPopper}
                    >
                        <IconClipboardCheck size={14} className="form-filter-icon" />
                        <span className="form-filter-label">
                            {selectedForm ? selectedForm.title : 'Formulário'}
                        </span>
                        {selectedForm ? (
                            <button className="form-filter-clear" onClick={clearFormFilter}>
                                <IconX size={12} />
                            </button>
                        ) : (
                            <IconChevronDown size={14} className="form-filter-icon" />
                        )}
                    </div>

                    <div className="search-input-wrapper">
                        <Input.Text
                            value={inputValue}
                            onInput={setInputValue}
                            onBlur={commitSearch}
                            onKeyDown={handleKeyDown}
                            placeholder="Buscar submissão por usuário ou email..."
                            start={<IconSearch size={16} />}
                            fullWidth
                        />
                    </div>

                    <Popper
                        controlRef={formPopperRef}
                        config={{ searchKeyExtractor: (opt, validate) => validate(opt.label) }}
                    />
                </div>
            </div>

            <div className="submissions-table-section">
                <div className="submissions-table-header">
                    <Text.h5 contrast={7}>
                        {loading
                            ? 'Carregando...'
                            : `${records} submiss${records !== 1 ? 'ões' : 'ão'}`}
                    </Text.h5>
                </div>

                <Table
                    id="community-submissions-table"
                    columns={columns}
                    data={submissions}
                    loading={loading}
                    loadingRows={5}
                    enablePagination={pages > 1}
                    page={page}
                    pages={pages}
                    records={records}
                    contained
                    onSelectPage={handleSelectPage}
                    onRowClick={handleViewSubmission}
                    emptyMessage="Nenhuma submissão encontrada"
                    emptySubtitle="As submissões de formulários da sua comunidade aparecerão aqui"
                    emptyIcon={<IconClipboardCheck size={36} />}
                    actionsComponent={(item) => (
                        <Button
                            size="small"
                            color="inverse"
                            endIcon={<IconArrowRight size={14} />}
                            onClick={() => handleViewSubmission(item)}
                        >
                            Ver detalhes
                        </Button>
                    )}
                />
            </div>
        </div>
    );
}
